import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut, Package, DollarSign, Truck, CheckCircle, Clock, XCircle,
  Search, User, Phone, Mail, MapPin, Home, Coffee, Filter, Lock,
  Loader2, RefreshCw, CreditCard, TrendingUp, Sun, Moon, Trash2, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { formatCOP } from '../store/cartStore';
import { calcOrderFinancials } from '../utils/financials';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = 'legatum-admin-token';
const ADMIN_THEME_KEY = 'legatum-admin-theme';

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  
  useEffect(() => {
    const stored = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (stored) setIsAuthenticated(true);
  }, []);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem(ADMIN_TOKEN_KEY, response.data.token);
        setIsAuthenticated(true);
        toast.success('Bienvenido al panel de administración');
      }
    } catch (error) {
      toast.error('Contraseña incorrecta');
    } finally {
      setLoggingIn(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setIsAuthenticated(false);
    setPassword('');
  };
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl border border-[#E5DEC5] p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#B58A3F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#B58A3F]" strokeWidth={1.5} />
              </div>
              <h1 className="heading-serif text-3xl text-[#142A1D] mb-2 font-light">
                Panel de Administración
              </h1>
              <p className="text-[#142A1D] opacity-60 text-sm">
                Ingresa la contraseña para continuar
              </p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                autoFocus
                data-testid="admin-password"
                className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#E5DEC5] rounded-xl text-[#142A1D] placeholder:text-[#142A1D]/40 focus:outline-none focus:border-[#B58A3F] transition-colors"
              />
              <motion.button
                type="submit"
                disabled={loggingIn || !password}
                data-testid="admin-login-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Iniciar Sesión'}
              </motion.button>
            </form>
            
            <button
              onClick={() => navigate('/')}
              className="w-full mt-4 text-sm text-[#142A1D] opacity-60 hover:opacity-100 transition-opacity"
            >
              ← Volver a la tienda
            </button>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return <AdminDashboard onLogout={handleLogout} />;
};

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [theme, setTheme] = useState(() => localStorage.getItem(ADMIN_THEME_KEY) || 'light');
  const [wipeStep, setWipeStep] = useState(0); // 0=closed, 1=first confirm, 2=type-to-confirm
  const [wipeInput, setWipeInput] = useState('');
  const [wiping, setWiping] = useState(false);
  
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  const headers = { 'X-Admin-Password': token };
  
  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem(ADMIN_THEME_KEY, next);
  };
  
  const handleWipe = async () => {
    setWiping(true);
    try {
      const res = await axios.post(
        `${API}/admin/orders/wipe?confirm=BORRAR-TODO`,
        {},
        { headers }
      );
      toast.success(`${res.data.deleted_count} pedidos eliminados. Próximo será ${res.data.next_order_number}`);
      setWipeStep(0);
      setWipeInput('');
      await fetchData();
    } catch (e) {
      toast.error('No se pudo limpiar. Verifica tu contraseña de admin.');
    } finally {
      setWiping(false);
    }
  };
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        onLogout();
      }
      toast.error('Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const updateOrderStatus = async (orderNumber, newStatus) => {
    try {
      await axios.patch(
        `${API}/orders/${orderNumber}/status?status=${newStatus}`,
        {},
        { headers }
      );
      toast.success(`Pedido ${orderNumber} actualizado a ${newStatus}`);
      fetchData();
      if (selectedOrder?.order_number === orderNumber) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      toast.error('Error al actualizar el pedido');
    }
  };
  
  const filteredOrders = orders.filter((order) => {
    if (filterStatus !== 'all' && order.status !== filterStatus) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (
        order.order_number.toLowerCase().includes(s) ||
        order.customer.name.toLowerCase().includes(s) ||
        order.customer.email.toLowerCase().includes(s) ||
        order.customer.phone.includes(s) ||
        order.customer.city.toLowerCase().includes(s)
      );
    }
    return true;
  });
  
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
    shipped: 'bg-purple-100 text-purple-800 border-purple-300',
    delivered: 'bg-green-100 text-green-800 border-green-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300'
  };
  
  const paymentStatusColors = {
    pending: 'bg-gray-100 text-gray-800',
    paid: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  
  const statusLabels = {
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    shipped: 'Enviado',
    delivered: 'Entregado',
    cancelled: 'Cancelado'
  };
  
  return (
    <div className={`admin-themed ${theme === 'dark' ? 'admin-dark' : 'admin-light'}`}>
      {/* Header */}
      <header className="border-b sticky top-0 z-40" style={{ background: 'var(--admin-header-bg)', borderColor: 'var(--admin-border)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_legatum-coffee/artifacts/dumgss85_LOGO2.webp"
              alt="Legatum"
              className="h-10 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="hidden md:block">
              <p className="text-xs text-[#B58A3F] uppercase tracking-widest">Admin</p>
              <p className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>Panel de Pedidos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              data-testid="admin-theme-toggle"
              className="p-2 rounded-lg transition-colors hover:opacity-70"
              style={{ background: 'var(--admin-subtle)', color: 'var(--admin-text)' }}
              title={theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
              aria-label={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" strokeWidth={1.5} />
              ) : (
                <Sun className="w-5 h-5 text-[#B58A3F]" strokeWidth={1.5} />
              )}
            </button>
            <button
              onClick={fetchData}
              className="p-2 rounded-lg transition-colors hover:opacity-70"
              style={{ background: 'var(--admin-subtle)', color: 'var(--admin-text)' }}
              title="Recargar"
            >
              <RefreshCw className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => setWipeStep(1)}
              data-testid="admin-wipe-btn"
              className="p-2 rounded-lg transition-colors hover:opacity-70"
              style={{ background: 'var(--admin-subtle)', color: '#dc2626' }}
              title="Vaciar todos los pedidos"
            >
              <Trash2 className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors hover:opacity-70"
              style={{ background: 'var(--admin-subtle)', color: 'var(--admin-text)' }}
            >
              <LogOut className="w-4 h-4" />
              Salir
            </button>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (() => {
          const netTotal = orders
            .filter(o => o.payment_status === 'paid')
            .reduce((acc, o) => acc + calcOrderFinancials(o).net, 0);
          return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <StatCard icon={Package} label="Total" value={stats.total_orders} color="text-[var(--admin-text)]" />
              <StatCard icon={Clock} label="Pendientes" value={stats.pending} color="text-yellow-600" />
              <StatCard icon={CheckCircle} label="Confirmados" value={stats.confirmed} color="text-blue-600" />
              <StatCard icon={Truck} label="Enviados" value={stats.shipped} color="text-purple-600" />
              <StatCard icon={DollarSign} label="Ingresos" value={formatCOP(stats.total_revenue)} color="text-[#B58A3F]" />
              <StatCard icon={TrendingUp} label="Neto" value={formatCOP(netTotal)} color="text-[#2B4C36]" highlight />
            </div>
          );
        })()}
        
        {/* Filters */}
        <div className="rounded-2xl border p-4 mb-6 flex flex-col md:flex-row gap-4" style={{ background: 'var(--admin-card)', borderColor: 'var(--admin-border)' }}>
          <div className="relative flex-1">
            <Search className="w-4 h-4 opacity-50 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--admin-text)' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por pedido, nombre, correo, teléfono o ciudad..."
              className="w-full pl-10 pr-4 py-2 border rounded-xl text-sm focus:outline-none focus:border-[#B58A3F]"
              style={{ background: 'var(--admin-input-bg)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 opacity-60" style={{ color: 'var(--admin-text)' }} />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-xl text-sm focus:outline-none focus:border-[#B58A3F]"
              style={{ background: 'var(--admin-input-bg)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="confirmed">Confirmados</option>
              <option value="shipped">Enviados</option>
              <option value="delivered">Entregados</option>
              <option value="cancelled">Cancelados</option>
            </select>
          </div>
        </div>
        
        {/* Orders Table */}
        <div className="rounded-2xl border overflow-hidden" style={{ background: 'var(--admin-card)', borderColor: 'var(--admin-border)' }}>
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 text-[#B58A3F] animate-spin mx-auto" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-12 text-center opacity-60" style={{ color: 'var(--admin-text)' }}>
              <Package className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p>No hay pedidos que coincidan con los filtros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-orders-table">
                <thead className="border-b" style={{ background: 'var(--admin-subtle)', borderColor: 'var(--admin-border)' }}>
                  <tr className="text-xs uppercase tracking-wider opacity-70" style={{ color: 'var(--admin-text)' }}>
                    <th className="px-4 py-3 text-left">Pedido</th>
                    <th className="px-4 py-3 text-left">Cliente</th>
                    <th className="px-4 py-3 text-left">Ciudad</th>
                    <th className="px-4 py-3 text-center">Bolsas</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Envío</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Pasarela</th>
                    <th className="px-4 py-3 text-right whitespace-nowrap">Neto</th>
                    <th className="px-4 py-3 text-center">Pago</th>
                    <th className="px-4 py-3 text-center">Estado</th>
                    <th className="px-4 py-3 text-center">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const fin = calcOrderFinancials(order);
                    return (
                    <tr
                      key={order.order_number}
                      onClick={() => setSelectedOrder(order)}
                      data-testid={`order-row-${order.order_number}`}
                      className="border-b hover:opacity-90 cursor-pointer transition-colors"
                      style={{ borderColor: 'var(--admin-border)' }}
                    >
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>{order.order_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>{order.customer.name}</p>
                          <p className="text-xs opacity-60" style={{ color: 'var(--admin-text)' }}>{order.customer.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--admin-text)' }}>{order.customer.city}</td>
                      <td className="px-4 py-3 text-center text-sm" style={{ color: 'var(--admin-text)' }}>{order.total_bags}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-[#B58A3F] whitespace-nowrap">{formatCOP(order.total)}</td>
                      <td className="px-4 py-3 text-right text-xs opacity-70 whitespace-nowrap" style={{ color: 'var(--admin-text)' }} data-testid="col-shipping">{formatCOP(fin.shipping)}</td>
                      <td className="px-4 py-3 text-right text-xs whitespace-nowrap" style={{ color: theme === 'dark' ? '#fca5a5' : '#b91c1c' }} data-testid="col-gateway">-{formatCOP(fin.gateway)}</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold whitespace-nowrap" style={{ color: theme === 'dark' ? '#B58A3F' : '#2B4C36' }} data-testid="col-net">{formatCOP(fin.net)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${paymentStatusColors[order.payment_status || 'pending']}`}>
                          {order.payment_status || 'pendiente'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[order.status]}`}>
                          {statusLabels[order.status] || order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-xs opacity-60 whitespace-nowrap" style={{ color: 'var(--admin-text)' }}>
                        {new Date(order.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Wipe Confirmation Modal */}
      <AnimatePresence>
        {wipeStep > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !wiping && setWipeStep(0)}
            className={`admin-themed ${theme === 'dark' ? 'admin-dark' : 'admin-light'} fixed inset-0 z-50 flex items-center justify-center p-4`}
            style={{ background: 'rgba(0,0,0,0.7)' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-2xl border-2 max-w-md w-full p-6"
              style={{ background: 'var(--admin-card)', borderColor: '#dc2626' }}
              data-testid="wipe-modal"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 rounded-full bg-red-100 flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--admin-text)' }}>
                    ¿Vaciar todos los pedidos?
                  </h3>
                  <p className="text-sm opacity-80" style={{ color: 'var(--admin-text)' }}>
                    Esta acción eliminará <strong>TODOS</strong> los pedidos permanentemente y reiniciará el contador. El próximo pedido será <strong>LEG-5001</strong>. Esta acción no se puede deshacer.
                  </p>
                </div>
              </div>

              {wipeStep === 1 ? (
                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => setWipeStep(0)}
                    className="flex-1 px-4 py-2 rounded-lg border font-medium"
                    style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => setWipeStep(2)}
                    data-testid="wipe-continue"
                    className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
                  >
                    Sí, quiero borrar todo
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2 opacity-70" style={{ color: 'var(--admin-text)' }}>
                    Para confirmar, escribe <span className="font-mono text-red-600 font-bold">BORRAR-TODO</span>
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={wipeInput}
                    onChange={(e) => setWipeInput(e.target.value)}
                    placeholder="BORRAR-TODO"
                    data-testid="wipe-input"
                    className="w-full px-3 py-2 border rounded-lg mb-4 font-mono focus:outline-none focus:border-red-600"
                    style={{ background: 'var(--admin-input-bg)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setWipeStep(0); setWipeInput(''); }}
                      disabled={wiping}
                      className="flex-1 px-4 py-2 rounded-lg border font-medium"
                      style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleWipe}
                      disabled={wipeInput !== 'BORRAR-TODO' || wiping}
                      data-testid="wipe-confirm"
                      className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    >
                      {wiping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      {wiping ? 'Borrando...' : 'Borrar todo'}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Order Detail Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onUpdateStatus={updateOrderStatus}
            statusColors={statusColors}
            statusLabels={statusLabels}
            paymentStatusColors={paymentStatusColors}
            theme={theme}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, highlight }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`rounded-2xl border p-4 shadow-sm ${
      highlight ? 'bg-gradient-to-br from-[#142A1D] to-[#2B4C36] border-[#B58A3F]/40 text-[#FDFBF7]' : ''
    }`}
    style={
      highlight
        ? undefined
        : { background: 'var(--admin-card)', borderColor: 'var(--admin-border)' }
    }
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className={`text-xs uppercase tracking-wider mb-1 ${highlight ? 'text-[#B58A3F]' : 'opacity-60'}`} style={highlight ? undefined : { color: 'var(--admin-text)' }}>{label}</p>
        <p className={`text-xl lg:text-2xl font-light heading-serif truncate ${highlight ? 'text-[#B58A3F]' : color}`}>{value}</p>
      </div>
      <Icon className={`w-6 h-6 flex-shrink-0 ml-2 ${highlight ? 'text-[#B58A3F]' : `${color} opacity-70`}`} strokeWidth={1.5} />
    </div>
  </motion.div>
);

const OrderDetailModal = ({ order, onClose, onUpdateStatus, statusColors, statusLabels, paymentStatusColors, theme }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className={`admin-themed ${theme === 'dark' ? 'admin-dark' : 'admin-light'} fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto`}
      style={{ background: 'rgba(0,0,0,0.6)', minHeight: '100vh' }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl border max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--admin-bg)', borderColor: 'var(--admin-border)' }}
      >
        <div className="sticky top-0 bg-[#142A1D] text-[#FDFBF7] p-6 flex justify-between items-start">
          <div>
            <p className="text-xs text-[#B58A3F] uppercase tracking-widest mb-1">Pedido</p>
            <h2 className="heading-serif text-2xl font-light">{order.order_number}</h2>
            <div className="flex gap-2 mt-2">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${statusColors[order.status]}`}>
                {statusLabels[order.status]}
              </span>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${paymentStatusColors[order.payment_status || 'pending']}`}>
                Pago: {order.payment_status || 'pendiente'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-[#FDFBF7] hover:text-[#B58A3F] transition-colors">
            <XCircle className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Customer Info */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#B58A3F] mb-3 font-medium">Cliente</h3>
            <div className="rounded-xl border p-4 space-y-2" style={{ background: 'var(--admin-card)', borderColor: 'var(--admin-border)' }}>
              <InfoRow icon={User} label="Nombre" value={order.customer.name} />
              <InfoRow icon={Phone} label="Teléfono" value={order.customer.phone} />
              <InfoRow icon={Mail} label="Correo" value={order.customer.email} />
              <InfoRow icon={Home} label="Dirección" value={order.customer.address} />
              <InfoRow icon={MapPin} label="Ciudad" value={order.customer.city} />
              {order.customer.notes && <InfoRow icon={Coffee} label="Notas" value={order.customer.notes} />}
            </div>
          </section>
          
          {/* Items */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#B58A3F] mb-3 font-medium">Productos</h3>
            <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--admin-card)', borderColor: 'var(--admin-border)' }}>
              {order.items.map((item, idx) => (
                <div key={idx} className="p-4 border-b last:border-0" style={{ borderColor: 'var(--admin-border)' }}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium" style={{ color: 'var(--admin-text)' }}>Café Legatum</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="inline-block px-2 py-0.5 bg-[#B58A3F]/20 text-[#B58A3F] rounded text-xs font-medium">
                          {item.profile}
                        </span>
                        <span className="inline-block px-2 py-0.5 bg-[#2B4C36]/20 text-[#2B4C36] rounded text-xs font-medium" style={{ color: theme === 'dark' ? '#8BB79A' : '#2B4C36' }}>
                          {item.grind}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>x{item.quantity}</p>
                      <p className="text-sm text-[#B58A3F]">{formatCOP(item.unit_price * item.quantity)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Totals + Financials */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#B58A3F] mb-3 font-medium">Resumen Financiero</h3>
            <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--admin-card)', borderColor: 'var(--admin-border)' }}>
              {/* Revenue breakdown */}
              <div className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="opacity-70" style={{ color: 'var(--admin-text)' }}>Subtotal ({order.total_bags} bolsas)</span>
                  <span className="font-medium" style={{ color: 'var(--admin-text)' }}>{formatCOP(order.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="opacity-70" style={{ color: 'var(--admin-text)' }}>Envío ({order.is_bogota ? 'Bogotá' : 'Nacional'})</span>
                  <span className="font-medium" style={{ color: 'var(--admin-text)' }}>{formatCOP(order.shipping_cost)}</span>
                </div>
                <div className="pt-2 border-t flex justify-between items-baseline" style={{ borderColor: 'var(--admin-border)' }}>
                  <span className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>Total Cobrado al Cliente</span>
                  <span data-testid="modal-total" className="text-xl font-light text-[#B58A3F] heading-serif">{formatCOP(order.total)}</span>
                </div>
              </div>

              {/* Cost breakdown - darker section */}
              {(() => {
                const fin = calcOrderFinancials(order);
                return (
                  <div className="bg-gradient-to-br from-[#142A1D] to-[#2B4C36] p-4 space-y-2 border-t-2 border-[#B58A3F]">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#B58A3F] font-medium mb-2">Análisis de Ingreso</p>

                    <div className="flex justify-between items-start text-sm">
                      <div className="flex items-start gap-2">
                        <CreditCard className="w-4 h-4 text-[#B58A3F] mt-0.5" strokeWidth={1.5} />
                        <div>
                          <span className="text-[#FDFBF7] block">Costo Pasarela Wompi</span>
                          <span className="text-[10px] text-[#F2EBE1] opacity-60">2.65% + $700 + IVA</span>
                        </div>
                      </div>
                      <span data-testid="modal-gateway" className="text-red-300 font-medium whitespace-nowrap">-{formatCOP(fin.gateway)}</span>
                    </div>

                    <div className="flex justify-between items-start text-sm">
                      <div className="flex items-start gap-2">
                        <Truck className="w-4 h-4 text-[#B58A3F] mt-0.5" strokeWidth={1.5} />
                        <div>
                          <span className="text-[#FDFBF7] block">Costo de Envío</span>
                          <span className="text-[10px] text-[#F2EBE1] opacity-60">{order.is_bogota ? 'Bogotá' : 'Nacional'} · {order.total_bags} {order.total_bags === 1 ? 'bolsa' : 'bolsas'}</span>
                        </div>
                      </div>
                      <span className="text-red-300 font-medium whitespace-nowrap">-{formatCOP(fin.shipping)}</span>
                    </div>

                    <div className="pt-3 mt-2 border-t border-[#B58A3F]/40 flex justify-between items-baseline">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#B58A3F]" strokeWidth={1.5} />
                        <span className="text-base font-medium text-[#FDFBF7]">Ingreso Neto</span>
                      </div>
                      <span data-testid="modal-net" className="text-2xl font-light text-[#B58A3F] heading-serif whitespace-nowrap">{formatCOP(fin.net)}</span>
                    </div>
                  </div>
                );
              })()}

              {order.wompi_transaction_id && (
                <p className="text-xs opacity-60 p-3 border-t break-all" style={{ color: 'var(--admin-text)', borderColor: 'var(--admin-border)' }}>
                  Wompi ID: {order.wompi_transaction_id}
                </p>
              )}
            </div>
          </section>
          
          {/* Actions */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#B58A3F] mb-3 font-medium">Cambiar Estado</h3>
            <div className="grid grid-cols-2 gap-2">
              {['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map((s) => (
                <button
                  key={s}
                  onClick={() => onUpdateStatus(order.order_number, s)}
                  disabled={order.status === s}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    order.status === s
                      ? `${statusColors[s]} opacity-50 cursor-not-allowed`
                      : 'hover:border-[#B58A3F]'
                  }`}
                  style={
                    order.status === s
                      ? undefined
                      : { background: 'var(--admin-card)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }
                  }
                >
                  {statusLabels[s]}
                </button>
              ))}
            </div>
          </section>
        </div>
      </motion.div>
    </motion.div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-[#B58A3F] flex-shrink-0 mt-1" strokeWidth={1.5} />
    <div className="flex-1 min-w-0">
      <p className="text-xs opacity-60 uppercase tracking-wider" style={{ color: 'var(--admin-text)' }}>{label}</p>
      <p className="text-sm font-medium break-words" style={{ color: 'var(--admin-text)' }}>{value}</p>
    </div>
  </div>
);

export default AdminPage;
